import { useTranslation } from "react-i18next";
import { useSAPDataStore } from "../store/SAPDataStore";
import { useTheme, useThemeClasses } from "../providers/ThemeProvider";
import ShiftSelector from "./ShiftSelector";
import Tabs from "./Tabs";
import DateRangeField from "./DateRangeField";
import { MachineTargetsButton } from "./targets/MachineTargets";
import EfficiencyTab from "./tabs/EfficiencyTab";
import OutputTab from "./tabs/OutputTab";
import DowntimeTab from "./tabs/DowntimeTab";
import RejectsTab from "./tabs/RejectsTab";
import MTBFTab from "./tabs/MTBFTab";
import FaultsTab from "./tabs/FaultsTab";
import { styles } from "../styles";
import { useState, useCallback, useRef, memo } from "react";

const tabs = ["Efficiency", "Output", "Downtime", "Rejects", "MTBF", "Faults"];

const tabKeys = [
  "efficiency",
  "output",
  "downtime",
  "rejects",
  "mtbf",
  "faults",
];

interface OverviewProps {
  machineId?: string;
  machineName?: string;
  machineData?: any;
  shifts: string[];
  setShifts: (shifts: string[]) => void;
  activeTab: string;
  setActiveTab: (tab: string) => void;
}

const Overview = ({
  machineId,
  shifts,
  setShifts,
  activeTab,
  setActiveTab,
}: OverviewProps) => {
  const { t } = useTranslation();
  const { getMachineById, getCachedMachineById } = useSAPDataStore();
  const { currentTheme } = useTheme();
  const themeClasses = useThemeClasses();

  // Track shift availability status
  const [hasAvailableShifts, setHasAvailableShifts] = useState(false);
  const [shiftsLoading, setShiftsLoading] = useState(true);

  const handleAvailabilityChange = useCallback(
    (available: boolean, loading: boolean) => {
      setHasAvailableShifts(available);
      setShiftsLoading(loading);
    },
    [],
  );

  // Get machine data - try SAP store first, then cached navigation history
  const machineData = machineId
    ? getMachineById(machineId) || getCachedMachineById(machineId)
    : null;
  // Use maschinennummer from data, or fallback to machineId (which is typically the serial number from URL)
  const maschinennummer = machineData?.maschinennummer || machineId;

  // Stabilize the `shifts` reference by content. ShiftSelector re-fetches and
  // calls setShifts() with a BRAND-NEW array (same content) on each render
  // cycle. When the Live Shift hook is active it fires several state updates as
  // worker messages arrive, causing multiple Overview re-renders — each one
  // produced a fresh `shifts` array, and since every chart's fetch effect keys
  // on the `shifts` reference, the charts refetched 3–5 times. Memoizing by
  // content (a stable signature) keeps the reference identical when the actual
  // selection hasn't changed, so charts fetch once.
  const shiftsKey = shifts.join("|");
  const stableShiftsRef = useRef<string[]>(shifts);
  if (stableShiftsRef.current.join("|") !== shiftsKey) {
    stableShiftsRef.current = shifts;
  }
  const stableShifts = stableShiftsRef.current;

  // Determine what message to show (if any)
  // machineHasNoData: only true if we explicitly have datenVerfuegbar === false
  // If machine not found or field is undefined, assume data might be available
  const machineHasNoData = machineData?.datenVerfuegbar === false;
  const noShiftsAvailable =
    !shiftsLoading && !hasAvailableShifts && !machineHasNoData;
  const noShiftsSelected =
    !shiftsLoading && hasAvailableShifts && shifts.length === 0;
  const shouldShowCharts =
    !shiftsLoading &&
    hasAvailableShifts &&
    shifts.length > 0 &&
    !machineHasNoData;

  let TabContent = null;
  switch (activeTab) {
    case "Efficiency":
      TabContent = (
        <EfficiencyTab
          machineSerialNo={maschinennummer}
          shifts={stableShifts}
        />
      );
      break;
    case "Output":
      TabContent = (
        <OutputTab machineSerialNo={maschinennummer} shifts={stableShifts} />
      );
      break;
    case "Downtime":
      TabContent = (
        <DowntimeTab
          machineSerialNo={maschinennummer || ""}
          shifts={stableShifts}
        />
      );
      break;
    case "Rejects":
      TabContent = (
        <RejectsTab
          machineSerialNo={maschinennummer || ""}
          shifts={stableShifts}
        />
      );
      break;
    case "MTBF":
      TabContent = (
        <MTBFTab machineSerialNo={maschinennummer} shifts={stableShifts} />
      );
      break;
    case "Faults":
      TabContent = (
        <FaultsTab machineSerialNo={maschinennummer} shifts={stableShifts} />
      );
      break;
    default:
      TabContent = null;
  }

  return (
    <main
      className={styles.overview.container}
      style={{
        backgroundColor: currentTheme.isDark
          ? "var(--bg-secondary)"
          : "#f3f4f6", // theme-aware background
        transition: "background-color 0.3s ease",
      }}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex flex-wrap items-center gap-3">
          <DateRangeField />
          <MachineTargetsButton machine={machineId ? getMachineById(machineId) : undefined} />
        </div>
        <div className="flex-shrink-0" data-tour="shift-selector">
          <ShiftSelector
            shifts={shifts}
            setShifts={setShifts}
            machineNumber={maschinennummer}
            className={styles.overview.shiftSelector.wrapper}
            onAvailabilityChange={handleAvailabilityChange}
          />
        </div>
      </div>
      {/*
        Keep the KPI tabs reachable while scrolling a long chart instead of losing
        them off the top — especially on mobile, where the charts are tall.

        The offset comes from `--app-header-height`, published by Header, because
        it isn't a constant: the header is 110px on mobile and 80px on desktop,
        taller again when a machine or line card is shown, and crops to 120px once
        scrolled. z-40 keeps this under the header's z-50 so it tucks beneath
        rather than sliding over it, and the background matches this view's own so
        content can't show through as it passes underneath.
      */}
      <div
        className="sticky z-40 pt-1 bg-gray-100 dark:bg-theme-bg-secondary"
        data-tour="kpi-tabs"
        style={{ top: "var(--app-header-height, 80px)" }}
      >
        <Tabs
          tabs={tabKeys.map((key) => t(key))}
          activeTab={t(activeTab.toLowerCase())}
          setActiveTab={(translatedTab) => {
            // Find index of the translated tab in the translated tabs array
            const translatedTabs = tabKeys.map((key) => t(key));
            const index = translatedTabs.findIndex(
              (tab) => tab.toLowerCase() === translatedTab.toLowerCase(),
            );
            if (index !== -1) {
              // Use original English tab name for internal state
              setActiveTab(tabs[index]);
            }
          }}
        />
      </div>
      <div className={styles.overview.content.container}>
        {/* Message when machine has no data */}
        {machineHasNoData && (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="text-6xl mb-4">📊</div>
            <h3
              className={`text-xl font-semibold mb-2 ${themeClasses.textPrimary}`}
            >
              {t("noDataAvailable", "No data available")}
            </h3>
            <p className={`text-center max-w-md ${themeClasses.textSecondary}`}>
              {t(
                "machineNoDataMessage",
                "This machine has no production data available.",
              )}
            </p>
          </div>
        )}

        {/* Message when no shifts available for the selected timeframe */}
        {noShiftsAvailable && (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="text-6xl mb-4">📅</div>
            <h3
              className={`text-xl font-semibold mb-2 ${themeClasses.textPrimary}`}
            >
              {t("noShiftsInTimeframe", "No shifts in selected timeframe")}
            </h3>
            <p className={`text-center max-w-md ${themeClasses.textSecondary}`}>
              {t(
                "selectDifferentTimeframe",
                "Try selecting a different date range where this machine has production data.",
              )}
            </p>
          </div>
        )}

        {/* Message when shifts available but none selected */}
        {noShiftsSelected && (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="text-6xl mb-4">☑️</div>
            <h3
              className={`text-xl font-semibold mb-2 ${themeClasses.textPrimary}`}
            >
              {t("noShiftSelected", "No shift selected")}
            </h3>
            <p className={`text-center max-w-md ${themeClasses.textSecondary}`}>
              {t(
                "pleaseSelectShift",
                "Please select at least one shift from the shift selector above to view the charts.",
              )}
            </p>
          </div>
        )}

        {/* Show charts only when shifts are selected */}
        {shouldShowCharts && TabContent}
      </div>
    </main>
  );
};

export default memo(Overview);
